import {
    ChangeDetectionStrategy,
    Component,
    effect,
    input,
    model,
    output,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Workflow } from "../service/Workflow";

export interface ContactRoleFieldRefRow {
    roleId: number | undefined;
    contactFieldRef: string;
}

@Component({
    standalone: true,
    selector: "s25-ng-workflow-contact-role-field-refs",
    template: `
        <div class="form-group">
            <label>Contact role field references (optional)</label>
            <small class="form-text">
                Each row: one role and a CONTACT field reference (e.g. $CNET-123-CONTACT) or submitter.
            </small>
            @for (mappingRow of fieldRefRows(); track $index) {
                <div class="mapping-row">
                    <s25-ng-dropdown-search-criteria
                        class="mapping-row__dropdown"
                        [type]="'organizationRoles'"
                        [value]="selectedRoleForRow(mappingRow)"
                        (valueChange)="onRoleChangeForRow($index, $event)"
                        [placeholder]="'Select role'"
                    />
                    <input
                        type="text"
                        class="form-control form-control--inline"
                        [ngModel]="mappingRow.contactFieldRef"
                        (ngModelChange)="onContactFieldRefChange($index, $event)"
                        placeholder="e.g. $CNET-456-CONTACT or submitter"
                    />
                    <button type="button" class="btn-remove" (click)="removeRow($index)" title="Remove">&times;</button>
                </div>
            }
            <button type="button" class="btn-add" (click)="addRow()">Add mapping</button>
        </div>
    `,
    styles: `
        .form-group {
            margin-bottom: 16px;
        }

        .form-group label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: #555;
        }

        .form-text {
            color: #666;
            font-size: 12px;
            margin-top: 4px;
            display: block;
            margin-bottom: 8px;
        }

        .mapping-row {
            display: flex;
            gap: 8px;
            align-items: center;
            margin-bottom: 8px;
        }

        .mapping-row__dropdown {
            flex: 1;
            min-width: 0;
            display: block;
            position: relative;
        }

        .form-control--inline {
            flex: 1;
            min-width: 0;
        }

        .btn-remove {
            flex-shrink: 0;
            width: 32px;
            height: 34px;
            padding: 0;
            border: 1px solid #ccc;
            border-radius: 4px;
            background: #f8f9fa;
            font-size: 18px;
            line-height: 1;
            cursor: pointer;
            color: #666;
        }

        .btn-remove:hover {
            background: #e9ecef;
            color: #333;
        }

        .btn-add {
            margin-top: 4px;
            padding: 6px 12px;
            border: 1px solid #2573a7;
            border-radius: 4px;
            background: #fff;
            color: #2573a7;
            font-size: 14px;
            cursor: pointer;
        }

        .btn-add:hover {
            background: #f0f7fc;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, S25DropdownPaginatedModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowContactRoleFieldRefsComponent {
    static readonly CONTACT_ROLE_FIELD_REFS_INPUT_CODE = "contactRoleFieldRefs";

    readonly inputs = model.required<Workflow.WorkflowActionInput[]>();
    readonly inputIdGenerator = input.required<() => number>();
    readonly changed = output<void>();

    readonly fieldRefRows = signal<ContactRoleFieldRefRow[]>([]);
    private initializedFromInputs = false;

    constructor() {
        effect(() => {
            const workflowInputs = this.inputs();
            if (this.initializedFromInputs) {
                return;
            }
            this.initializedFromInputs = true;
            const storedJson = workflowInputs.find(
                (workflowInput) =>
                    workflowInput.inputCode ===
                    WorkflowContactRoleFieldRefsComponent.CONTACT_ROLE_FIELD_REFS_INPUT_CODE,
            )?.inputValue;
            if (!storedJson?.trim()) {
                return;
            }
            try {
                const parsed = JSON.parse(storedJson) as {
                    items?: { roleId?: number; contactFieldRef?: string }[];
                };
                if (!Array.isArray(parsed?.items) || !parsed.items.length) {
                    return;
                }
                this.fieldRefRows.set(
                    parsed.items.map((item) => ({
                        roleId: item.roleId,
                        contactFieldRef: item.contactFieldRef ?? "",
                    })),
                );
            } catch {
                // ignore invalid JSON
            }
        });
    }

    selectedRoleForRow(row: ContactRoleFieldRefRow): DropDownItem | null {
        if (row.roleId == null || Number.isNaN(row.roleId)) {
            return null;
        }
        return {
            itemId: row.roleId,
            itemTypeId: undefined,
            itemName: "",
            txt: "",
            val: String(row.roleId),
        } as DropDownItem;
    }

    onRoleChangeForRow(rowIndex: number, role: DropDownItem | null) {
        this.fieldRefRows.update((rows) =>
            rows.map((row, index) =>
                index === rowIndex ? { ...row, roleId: role?.itemId != null ? Number(role.itemId) : undefined } : row,
            ),
        );
        this.serializeFieldRefRows();
    }

    onContactFieldRefChange(rowIndex: number, value: string) {
        this.fieldRefRows.update((rows) =>
            rows.map((row, index) => (index === rowIndex ? { ...row, contactFieldRef: value } : row)),
        );
        this.serializeFieldRefRows();
    }

    addRow() {
        this.fieldRefRows.update((rows) => [...rows, { roleId: undefined, contactFieldRef: "" }]);
        this.serializeFieldRefRows();
    }

    removeRow(rowIndex: number) {
        this.fieldRefRows.update((rows) => rows.filter((_, index) => index !== rowIndex));
        this.serializeFieldRefRows();
    }

    private serializeFieldRefRows() {
        const items = this.fieldRefRows()
            .filter((row) => row.roleId != null && row.contactFieldRef?.trim())
            .map((row) => ({
                roleId: row.roleId,
                contactFieldRef: row.contactFieldRef.trim(),
            }));
        this.setInputCodeValue(
            WorkflowContactRoleFieldRefsComponent.CONTACT_ROLE_FIELD_REFS_INPUT_CODE,
            items.length ? JSON.stringify({ items }) : "",
        );
    }

    private setInputCodeValue(inputCode: string, value: string) {
        const workflowInputs = this.inputs();
        let targetInput = workflowInputs.find((workflowInput) => workflowInput.inputCode === inputCode);
        if (!targetInput) {
            targetInput = {
                id: this.inputIdGenerator()(),
                inputCode,
                inputValue: value,
            };
            this.inputs.update((rows) => [...rows, targetInput!]);
        } else {
            targetInput.inputValue = value;
            this.inputs.update((rows) => [...rows]);
        }
        this.changed.emit();
    }
}
